<template>
  <div>
    <div
      class="general-reports-table"
      ref="general-reports-table"
    >
      <div
        v-if="groupsToShow.length > 1"
        class="primary darken-1 white--text"
      >
        <table class="table-container" cellpadding="0" cellspacing="0">
          <tr class="table-row">
            <th class="table-cell">
              Total Reported: <br>
              {{ calculateTotalsFromGroups('Real_Dur', groupsToShow) }}
            </th>
            <th class="table-cell">
              Total Paid: <br>
              {{ calculateTotalsFromGroups('Report_Dur', groupsToShow) }}
            </th>
            <th class="table-cell">
              Total Invoice: <br>
              {{ calculateTotalsFromGroups('Invoice_Dur', groupsToShow) }}
            </th>
          </tr>
        </table>
      </div>
      <div
        class="group-container"
        v-for="(group, groupKey) in groupsToShow"
        :key="'group_' + groupKey"
        :ref="group.collapse ? 'table-row': ''"
      >
        <div
          class="group-title pa-2 primary white--text d-flex align-center"
          :class="{
            'collapse': group.collapse
          }"
          v-if="group.value"
          @click.self="toggleGroup(group)"
        >
          <v-icon color="white" small class="mr-3">
            {{ group.collapse ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
          </v-icon>
          {{ group.title ? group.title : 'Other' }}
          <span class="ml-3">| Report: {{ calculateTotals('Real_Dur', group.items) }}</span>
          <span class="ml-1">| Paid: {{ calculateTotals('Report_Dur', group.items) }}</span>
          <span class="ml-1">| Invoice: {{ calculateTotals('Invoice_Dur', group.items) }}</span>
          <v-btn small outlined color="white"  class="ml-auto" @click.prevent="exportToXLSX(group)">
            Export To XLSX
          </v-btn>
        </div>
        <div
          v-if="!group.collapse"
        >
          <table class="table-container" cellpadding="0" cellspacing="0">
            <tr
              v-if="headers"
              class="table-row table-row--header"
            >
              <th
                style="width: 30px;"
                class="table-cell"
                v-if="canEdit"
              >
                #
              </th>
              <th
                class="table-cell"
                v-for="(header, headerKey) in group.headers"
                :key="`header_${groupKey}_${headerKey}`"
                :class="{
                  'black--text': group.sortBy.field === header.value
                }"
              >
                <div class="d-flex justify-space-between">
                  {{ header.text }}
                  <span v-if="['Real_Dur', 'Report_Dur', 'Invoice_Dur'].indexOf(header.value) > -1 && group.items.length">
                    ({{ calculateTotals(header.value, group.items) }})
                  </span>
                  <v-btn x-small icon class="ml-3" @click="sortByField(group, header.value)">
                    <v-icon small v-if="group.sortBy.field === header.value && group.sortBy.desc">mdi-arrow-up</v-icon>
                    <v-icon small v-else-if="group.sortBy.field === header.value && !group.sortBy.desc">mdi-arrow-down</v-icon>
                    <v-icon small v-else class="no-sort">mdi-arrow-up-down</v-icon>
                  </v-btn>
                </div>
              </th>
            </tr>
            <tr
              class="table-row"
              v-for="(item, itemKey) in itemsToShow(group, pagination)"
              :key="`item_${groupKey}_${itemKey}`"
              ref="table-row"
            >
              <td
                class="table-cell text-no-wrap"
                style="width: 30px;"
                v-if="canEdit && canCalculateTotals"
              >
                <v-tooltip bottom color="red darken-2">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      color="red darken-2"
                      v-bind="attrs"
                      v-on="on"
                      :disabled="editFields && activeReport.Reporting_ID === item.Reporting_ID"
                      @click="showRemoveDialog(group, item)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
                <v-tooltip bottom
                  :color="
                    editFields && activeReport.Reporting_ID === item.Reporting_ID ? 'primary' : 'success darken-2'
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="ml-3"
                      icon
                      x-small
                      :color="
                        editFields && activeReport.Reporting_ID === item.Reporting_ID ? 'primary' : 'success darken-2'
                      "
                      v-bind="attrs"
                      v-on="on"
                      @click="editRow(item)"
                    >
                      <v-icon>
                        {{ editFields && activeReport.Reporting_ID === item.Reporting_ID ? 'mdi-close-circle' : 'mdi-pencil' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>
                    {{ editFields && activeReport.Reporting_ID === item.Reporting_ID ? 'Stop Editing' : 'Edit' }}
                  </span>
                </v-tooltip>
              </td>
              <td
                class="table-cell"
                v-for="(header, headerKey) in group.headers"
                :class="[header.cellClass]"
                :key="headerKey"
              >
                <div
                  v-if="editFields && activeReport.Reporting_ID === item.Reporting_ID && canCalculateTotals"
                >
                  <!-- Durations -->
                  <v-menu
                    v-if="['Real_Dur', 'Report_Dur', 'Invoice_Dur'].indexOf(header.value) > -1"
                    :ref="`menu_${header.value}`"
                    v-model="menu[header.value]"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value.sync="item[header.value]"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="item[header.value]"
                        :label="header.text"
                        prepend-icon="mdi-clock-time-four-outline"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="menu[header.value]"
                      v-model="item[header.value]"
                      format="24hr"
                      :allowed-minutes="allowedMinutes"
                      full-width
                      @click:minute="updateReport(`menu_${header.value}`, item[header.value])"
                    ></v-time-picker>
                  </v-menu>
                  <!-- /Durations -->
                  <!-- Start Date -->
                  <DatePicker
                    v-else-if="'Start_Date' === header.value"
                    v-model="item[header.value]"
                    @onDatePickerChange="updateReport(false, item[header.value])"
                  />
                  <!-- /Start Date -->
                  <!-- Activity Type -->
                  <v-select
                    v-else-if="'Activity_Type_Description' === header.value > -1"
                    v-model="item['Activity_Type']"
                    :items="activityTypes"
                    item-text="Description"
                    item-value="Activity_Type"
                    label="Activity Type"
                    return-object
                    @change="updateReport(false, false, $event)"
                  ></v-select>
                  <!-- /Activity Type -->
                  <!-- Description -->
                  <div
                    v-else-if="'Description' === header.value"
                    class="cursor-pointer"
                    @click="showDescriptionEditor"
                  >
                    <v-textarea
                      readonly
                      v-model="item.Description"
                      hide-details
                      dense
                    ></v-textarea>
                  </div>
                  <!-- /Description -->
                  <span v-else>{{ item[header.value] }}</span>
                </div>
                <div v-else>
                  <span
                    @click="openEmployeeCard(item.Employee_ID)"
                    v-if="header.value === 'Employee_FullName'"
                    style="cursor: pointer; text-decoration: underline;"
                  >
                    {{ item[header.value] }}
                  </span>
                  <span
                    @click="openProjectCard(item.Project_ID)"
                    v-else-if="header.value === 'Project_Name'"
                    style="cursor: pointer; text-decoration: underline;"
                  >
                    {{ item[header.value] }}
                  </span>
                  <span
                    @click="openClientCard(item.Client_ID)"
                    v-else-if="header.value === 'Client_Name'"
                    style="cursor: pointer; text-decoration: underline;"
                  >
                    {{ item[header.value] }}
                  </span>
                  <span v-else>
                    {{ item[header.value] }}
                  </span>
                </div>
              </td>
            </tr>
            <tr v-if="!sortedItems.length || !groupsToShow[0].items.length">
              <td :colspan="headers.length" class="text-center py-3">
                No result
              </td>
            </tr>
          </table>
        </div>
      </div>
      <!-- Remove report dialog -->
      <v-dialog
        v-model="removeDialog"
        max-width="500"
      >
        <v-card>
          <v-card-title>Are you sure?</v-card-title>
          <v-divider></v-divider>
          <v-card-text class="py-3">
            You can't restore data after delete!
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn color="primary" outlined small @click="closeRemoveDialog">Cancel</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="red darken-2 white--text" small @click="removeReport">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- /Remove report dialog -->
      <!-- Description editor dialog -->
      <v-dialog
        v-if="showDescriptionDialog"
        v-model="showDescriptionDialog"
        min-width="290"
        max-width="720"
      >
        <v-card class="pt-4">
          <v-card-text>
            <DescriptionWithSpellCheck
              :key="`description-text-area-${showDescriptionDialog}`"
              label="Description"
              v-model="tempDescription"
            />
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn
              color="primary"
              outlined
              @click.prevent="showDescriptionDialog = !showDescriptionDialog; tempDescription = null"
            >
              Cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              v-if="tempDescription && tempDescription !== activeReport.Description"
              @click.prevent="saveDescription"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- /Description editor dialog -->
    </div>
    <div class="mt-2">
      <v-btn small outlined color="primary" @click="exportToXLSX(groupsToShow)">
        Export To XLSX
      </v-btn>
    </div>
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

const xlsx = require('json-as-xlsx')

export default {
  name: 'GeneralReportsTable',
  props: {
    headers: {
      required: false
    },
    items: {
      required: false,
      default: []
    },
    groupBy: {
      required: false,
      default: []
    },
    pagination: {
      required: false
    },
    search: {
      required: false
    }
  },
  components: {
    DescriptionWithSpellCheck: () => import('@/components/reporting/DescriptionWithSpellCheck.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  data () {
    return {
      removeDialog: false,
      activeReport: null,
      activeGroup: null,
      editFields: false,
      tempDescription: null,
      showDescriptionDialog: false,
      canCalculateTotals: true,
      menu: {
        Start_Date: false,
        Real_Dur: false,
        Report_Dur: false,
        Invoice_Dur: false
      },
      sortBy: {
        field: null,
        desc: false,
        countClicks: 0
      },
      observerWatcher: null,
      groups: []
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    updatedPagination () {
      return this.pagination
    },
    filteredGroups () {
      const r = new RegExp(this.search, 'gi')
      return this.search && this.groups.length > 1
        ? this.groups.filter(gr => {
          r.lastIndex = 0
          const grTest = gr.title ? r.test(gr.title) : 'Other'
          return grTest || !!this.sortedItems(gr).length
        })
        : this.groups
    },
    groupsToShow () {
      return this.filteredGroups.length > 1 ? [...this.filteredGroups].splice(this.pagination.from, this.pagination.to - this.pagination.from) : [...this.filteredGroups]
    },
    canEdit () {
      const roles = this.$store.getters.getEmployeeRoles
      const rolesArr = []
      for (const key in roles) {
        if (['Financier', 'HR', 'Manager'].indexOf(roles[key].name) > -1 && roles[key].val) rolesArr.push(roles[key])
      }
      return !!rolesArr.length
    },
    currentHeaders () {
      return [...this.headers]
    },
    sortedItems () {
      return group => {
        const r = new RegExp(this.search, 'gi')
        const res = [...group.items]
        if (group.sortBy.field) {
          res.sort((a, b) => {
            if (a[group.sortBy.field] < b[group.sortBy.field]) {
              return group.sortBy.desc ? 1 : -1
            }
            if (a[group.sortBy.field] > b[group.sortBy.field]) {
              return group.sortBy.desc ? -1 : 1
            }
            return 0
          })
        }
        return this.search ? res.filter(item => {
          r.lastIndex = 0
          let str = ''
          for (const key in item) {
            str += item[key]
          }
          return r.test(str)
        }) : res
      }
    },
    itemsToShow () {
      return (group, pagination) => {
        return this.filteredGroups.length > 1 ? [...this.sortedItems(group)] : [...this.sortedItems(group)].splice(pagination.from, pagination.to - pagination.from)
      }
    },
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    projects () {
      return this.$store.getters.getProjects
    },
    clients () {
      return this.$store.getters.getClients
    }
  },
  beforeDestroy () {
    if (this.observerWatcher) {
      this.observerWatcher.disconnect()
    }
  },
  mounted () {
    this.fillItems()
    this.getProjects()
    this.getClients()
  },
  methods: {
    getClients () {
      this.axios.get('/api/clients', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setClients', { clients: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getProjects () {
      this.axios.get('/api/projects', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        const proj = []
        for (let i = 0; i < response.data.length; i++) {
          let integratedWith = ''
          if (response.data[i].Integrated_With_Jira) integratedWith = 'Jira'
          if (response.data[i].Integrated_With_Redmine) integratedWith = 'Redmine'
          proj.push({
            ...response.data[i],
            Integrated_With: integratedWith
          })
        }
        this.$store.dispatch('setProjects', { projects: proj })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    exportToXLSX (group) {
      group = group.length ? group : [group]
      const data = []
      let columns = []
      if (this.groupBy && this.groupBy.length) {
        const fields = ['Employee_FullName', 'Project_Name', 'Task_Name', 'Start_Date', 'Real_Dur', 'Report_Dur', 'Invoice_Dur', 'Activity_Type', 'Description']
        this.groupBy.forEach(gr => {
          if (fields.indexOf(gr) === -1) fields.push(gr)
        })
        columns = this.headers.map(h => {
          if (fields.indexOf(h.value) > -1) {
            return {
              label: h.text,
              value: h.value
            }
          }
        }).filter(item => !!item)
      } else {
        columns = this.headers.map(h => {
          return {
            label: h.text,
            value: h.value
          }
        })
      }
      let itemsToExport = []
      for (let i = 0; i < group.length; i++) {
        for (let j = 0; j < group[i].items.length; j++) {
          itemsToExport.push(group[i].items[j])
        }
        if (!(this.groupBy && this.groupBy.length)) {
          data.push({
            sheet: 'Reports' + (group[i].title ? `: ${group[i].title}` : ''),
            columns: columns,
            content: itemsToExport
          })
          itemsToExport = []
        }
      }
      if (this.groupBy && this.groupBy.length) {
        data.push({
          sheet: 'Reports',
          columns: columns,
          content: itemsToExport
        })
      }
      xlsx(data, { fileName: 'Reports' })
    },
    showDescriptionEditor () {
      this.tempDescription = this.activeReport.Description
      this.showDescriptionDialog = true
    },
    saveDescription () {
      this.activeReport.Description = this.tempDescription
      this.showDescriptionDialog = false
      this.updateReport(false, false, false)
    },
    allowedMinutes (m) {
      return m === 30 || m === 0
    },
    editRow (item) {
      if (this.activeReport && this.activeReport.Reporting_ID === item.Reporting_ID) {
        this.editFields = !this.editFields
        if (!this.editFields) this.activeReport = null
      } else {
        this.activeReport = item
        this.editFields = true
      }
    },
    showRemoveDialog (group, report) {
      this.activeReport = report
      this.activeGroup = group
      this.removeDialog = true
    },
    closeRemoveDialog () {
      this.activeReport = null
      this.activeGroup = null
      this.removeDialog = false
    },
    calculateTotals (key, items) {
      let total = 0
      if (this.canCalculateTotals) {
        for (let i = 0; i < items.length; i++) {
          const minutes = 100 * +items[i][key].split(':')[1] / 60 / 100
          const hours = +items[i][key].split(':')[0]
          total += (hours + minutes)
        }
      }
      return total.toFixed(2)
    },
    calculateTotalsFromGroups (key, groups) {
      let total = 0
      if (this.canCalculateTotals) {
        for (let i = 0; i < groups.length; i++) {
          for (let j = 0; j < groups[i].items.length; j++) {
            const minutes = 100 * +groups[i].items[j][key].split(':')[1] / 60 / 100
            const hours = +groups[i].items[j][key].split(':')[0]
            total += (hours + minutes)
          }
        }
      }
      return total.toFixed(2)
    },
    toggleGroup (group) {
      group.collapse = !group.collapse
      setTimeout(() => {
        this.initIO()
      })
    },
    groupItems (from, to) {
      for (let i = from; i < to; i++) {
        if (!this.items[i]) break
        let val = ''
        const title = []
        this.groupBy.forEach(gr => {
          val += this.items[i][gr] ? this.items[i][gr] : 'Other'
          title.push(this.items[i][gr] ? this.items[i][gr] : 'Other')
        })
        const groupIndex = this.groups.findIndex(item => item.value === val)
        if (groupIndex > -1) {
          this.groups[groupIndex].items.push(this.items[i])
        } else {
          this.groups.push({
            value: val,
            title: '' + title.join('/'),
            collapse: true,
            sortBy: { ...this.sortBy },
            items: [{
              ...this.items[i]
            }],
            headers: this.currentHeaders
          })
        }
      }
      if (to < this.items.length) {
        setTimeout(() => {
          this.groupItems(to, to + 1000)
        })
      } else {
        this.$emit('groupChange', this.filteredGroups.length)
        this.canCalculateTotals = true
      }
    },
    fillItems () {
      this.groups = []
      this.canCalculateTotals = false
      if (this.groupBy && this.groupBy.length) {
        this.groupItems(0, 1000)
        this.groups.sort((a, b) => {
          if (a.title < b.title) {
            return -1
          }
          if (a.title > b.title) {
            return 1
          }
          return 0
        })
      } else {
        this.groups.push({
          value: null,
          title: null,
          collapse: false,
          sortBy: { ...this.sortBy },
          items: [...this.items],
          headers: this.currentHeaders
        })
        this.$emit('groupChange', 1)
      }
      this.canCalculateTotals = true
      setTimeout(() => {
        this.initIO()
      })
    },
    initIO () {
      if (this.$refs['table-row'] && this.$refs['table-row'].length) {
        if (this.observerWatcher) {
          this.observerWatcher.disconnect()
        }
        this.setIntesectionObserver()
      }
      this.$store.dispatch('showOverlay', { show: false })
    },
    setIntesectionObserver () {
      const options = {
        root: this.$refs['general-reports-table'],
        rootMargin: '300px',
        threshold: [0.25, 0.75]
      }
      this.observerWatcher = new IntersectionObserver(this.observerCallback, options)
      const elms = this.$refs['table-row']
      for (let i = 0; i < elms.length; i++) {
        this.observerWatcher.observe(elms[i])
      }
    },
    observerCallback (entries, observer) {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          entry.target.style.visibility = 'visible'
        } else {
          entry.target.style.visibility = 'hidden'
        }
      })
    },
    sortByField (group, field) {
      setTimeout(() => {
        if (field === group.sortBy.field) {
          if (group.sortBy.countClicks < 1) {
            group.sortBy.desc = !group.sortBy.desc
            group.sortBy.countClicks++
          } else {
            group.sortBy.field = null
            group.desc = false
            group.sortBy.countClicks = 0
          }
        } else {
          group.sortBy.field = field
          group.sortBy.desc = false
          group.sortBy.countClicks = 0
        }
      })
    },
    removeReport () {
      this.axios.delete(`/api/reporting/${this.activeReport.Reporting_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const i = this.activeGroup.items.findIndex(item => item.Reporting_ID === this.activeReport.Reporting_ID)
        if (i > -1) {
          this.activeGroup.items.splice(i, 1)
        }
        this.$emit('removeReport', this.activeReport.Reporting_ID)
        this.closeRemoveDialog()
        this.$store.dispatch('setSuccess', { successMsg: 'Report successfully removed' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateReport (ref, val, at) {
      if (ref && val) {
        this.$refs[ref][0].save(val)
      }
      if (at) {
        this.activeReport.Activity_Type = at.Activity_Type
        this.activeReport.Activity_Type_Description = at.Description
      }
      this.axios.put(`/api/reporting/${this.activeReport.Reporting_ID}`, {
        Project_ID: this.activeReport.Project_ID,
        Task_ID: this.activeReport.Task_ID,
        Employee_ID: this.activeReport.Employee_ID,
        Start_Date: this.activeReport.Start_Date,
        Start_Time: '00:00',
        Real_Dur: this.activeReport.Real_Dur,
        Report_Dur: this.activeReport.Report_Dur,
        Invoice_Dur: this.activeReport.Invoice_Dur,
        Description: this.activeReport.Description,
        Activity_Type: this.activeReport.Activity_Type
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$emit('updateReport', this.activeReport)
        this.$store.dispatch('setSuccess', { successMsg: 'Report successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    openEmployeeCard (employeeID) {
      this.axios.get(`/api/employee/${employeeID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('employeeDetailsScreen', {
          show: true,
          employee: res.data[0],
          edit: false
        })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    openProjectCard (projectID) {
      const proj = this.projects.filter(p => p.Project_ID === projectID)[0]
      if (proj) {
        proj.Get_Date = proj.Get_Date ? formatLocalDate(proj.Get_Date) : null
        proj.Start_Date = proj.Start_Date ? formatLocalDate(proj.Start_Date) : null
        proj.Release_Date = proj.Release_Date ? formatLocalDate(proj.Release_Date) : null
        this.$store.dispatch('projectDetailsScreen', {
          show: true,
          project: proj
        })
      } else {
        this.$store.dispatch('setError', { errorMsg: 'Something went wrong!' })
      }
    },
    openClientCard (clientID) {
      const client = this.clients.filter(c => c.Client_ID === clientID)[0]
      if (client) {
        this.$store.dispatch('clientDetailsScreen', {
          show: true,
          client: client
        })
      } else {
        this.$store.dispatch('setError', { errorMsg: 'Something went wrong!' })
      }
    }
  },
  watch: {
    groupBy () {
      this.$store.dispatch('showOverlay', { show: true })
      setTimeout(() => {
        this.fillItems()
      })
    },
    pagination: {
      handler () {
        setTimeout(() => {
          this.initIO()
        })
      },
      deep: true
    }
  }
}
</script>
<style lang="scss" scoped>
  .collapse {
    border-bottom: 1px solid #ffffff !important;
  }
  .group-container {
    &:last-child {
      .collapse {
        border-bottom: none;
      }
    }
  }

  .group-title {
    cursor: pointer;
  }
  .general-reports-table {
    overflow: auto;
    font-size: 0.75rem;
    max-height: 62vh;
  }
  .table-container {
    width: 100%;
    position: relative;
  }
  .table-row {
    .table-cell {
      border-bottom: 1px solid rgba(0, 0, 0, .25);
    }
    &--header {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: #fff;
      .table-cell {
        font-size: 0.75rem;
        font-weight: 700;
        white-space: nowrap;
      }
    }
  }
  .table-cell {
    padding: 5px 10px;
    font-size: 0.9rem;
    white-space: pre-wrap;
    border-left: 1px solid rgba(0, 0, 0, .25);
    &:first-child {
      border-left: none;
    }
  }

  .no-sort {
    opacity: 0.5;
    &:hover {
      opacity: 1;
    }
  }

</style>
